import { useCallback, useEffect, useRef } from "react";
import type { Exercise, PlanStarter } from "@heraion/shared";
import { useWorkoutStarter as useSharedWorkoutStarter } from "@heraion/ui";

import { apiFetch } from "./api";
import { useMaxesDataWeb } from "./maxes/useMaxesDataWeb";
import { useShellProfile } from "./ThemeProfileBridge";

// Web mount of the shared start orchestration (@heraion/ui useWorkoutStarter,
// CN-workoutstarter-twin). The three seams: MAXes from the shared
// useMaxesData layer, bodyweight handed in by the screen that already holds
// the profile, and the hand-off to AppShell, which opens the Logger on the
// Workouts tab (web-rnw's LoggerScreen takes `planStarter` as a prop). The
// MAXes it read go back too, once loaded: the Library's preview bands (#358).
// #343: the loads take the bodyweight off a bodyweight exercise's MAX, so a
// start waits for the profile read, like every other write of a load.
export function useWorkoutStarter(
  catalog: Exercise[] | null,
  bodyweightKg: number | null,
  onStarter: (starter: PlanStarter) => void,
) {
  const { getFreshDetailed, maxMeta, loaded } = useMaxesDataWeb();
  const docRead = useShellProfile().doc !== null;
  const bodyweightRef = useRef(bodyweightKg);
  const waiting = useRef<Array<() => void>>([]);
  useEffect(() => {
    bodyweightRef.current = bodyweightKg;
    if (!docRead) return;
    const ready = waiting.current;
    waiting.current = [];
    for (const go of ready) go();
  }, [docRead, bodyweightKg]);
  const getBodyweightKg = useCallback(
    () =>
      docRead
        ? Promise.resolve(bodyweightKg)
        : new Promise<number | null>((resolve) => {
            waiting.current.push(() => resolve(bodyweightRef.current));
          }),
    [docRead, bodyweightKg],
  );
  const starter = useSharedWorkoutStarter({
    apiFetch,
    catalog,
    getMaxesDetailed: getFreshDetailed,
    getBodyweightKg,
    onStarter,
  });
  return { ...starter, maxMeta: loaded ? maxMeta : undefined };
}
